(() => {
  if (window.TodayMerchStudioConfigCompat) return;

  const isInactive = (value) => value === false || value === 'false';
  const asArray = (value) => (Array.isArray(value) ? value : []);
  const asString = (value) => String(value || '').trim();

  const normalizePart = (part = {}, fallbackId = '') => {
    const shape = asString(part.shape).toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const curvature = Math.max(-40, Math.min(40, Number(part.curvature || 0)));
    return {
      id: asString(part.id || fallbackId),
      x: Number(part.x ?? 32),
      y: Number(part.y ?? 34),
      width: Number(part.width ?? 36),
      height: Number(part.height ?? 24),
      rotation: Number(part.rotation ?? part.angle ?? 0),
      ...(shape === 'circle' ? { shape } : {}),
      ...(curvature ? { curvature } : {}),
    };
  };

  const getBounds = (parts = []) => {
    if (!parts.length) return { x: 32, y: 34, width: 36, height: 24 };
    const left = Math.min(...parts.map((part) => part.x));
    const top = Math.min(...parts.map((part) => part.y));
    const right = Math.max(...parts.map((part) => part.x + part.width));
    const bottom = Math.max(...parts.map((part) => part.y + part.height));
    return {
      x: left,
      y: top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
    };
  };

  const buildLegacyVariantMedia = (variantMediaV2, placements, mediaViews) => {
    const placementById = new Map(placements.map((placement) => [asString(placement.id), placement]));
    const mediaViewById = new Map(mediaViews.map((view) => [asString(view.id), view]));
    const variants = {};

    Object.entries(variantMediaV2 && typeof variantMediaV2 === 'object' ? variantMediaV2 : {}).forEach(([variantId, assignments]) => {
      if (!assignments || typeof assignments !== 'object' || Array.isArray(assignments)) return;
      const media = {};
      const gallery = [];
      const seenViewIds = new Set();

      Object.entries(assignments).forEach(([placementId, rawViewId]) => {
        const viewId = asString(rawViewId?.viewId || rawViewId);
        const placement = placementById.get(asString(placementId));
        const view = mediaViewById.get(viewId);
        if (!placement || !view || isInactive(placement.active)) return;

        const assignment = {
          placementKey: asString(placement.id),
          label: asString(placement.label),
          viewKey: viewId,
          mediaId: asString(view.mediaId),
          fileId: asString(view.mediaId),
          image: asString(view.imageUrl),
          thumbnail: asString(view.imageUrl),
          alt: asString(view.alt),
          method: 'studio-v2',
          confidence: 1,
        };
        media[asString(placement.id)] = assignment;
        if (!seenViewIds.has(viewId)) {
          gallery.push(assignment);
          seenViewIds.add(viewId);
        }
      });

      variants[variantId] = { variantId, media, gallery };
    });

    return { version: 2, strategy: 'v2', variants };
  };

  const adapt = (rawConfig = {}) => {
    if (
      Number(rawConfig?.schemaVersion) !== 2 ||
      !Array.isArray(rawConfig?.mediaViews) ||
      !Array.isArray(rawConfig?.placements)
    ) {
      return rawConfig;
    }

    const mediaViews = rawConfig.mediaViews.filter((view) => view && typeof view === 'object');
    const placements = rawConfig.placements.filter((placement) => placement && typeof placement === 'object');
    const views = mediaViews.map((view, index) => {
      const id = asString(view.id || `media-${index + 1}`);
      const linkedPlacements = placements.filter((placement) => (
        asArray(placement.instances).some((instance) => asString(instance?.viewId) === id)
      ));
      const activeLinkedPlacement = linkedPlacements.find((placement) => !isInactive(placement.active));
      return {
        key: id,
        label: asString(activeLinkedPlacement?.label || linkedPlacements[0]?.label || view.alt || id),
        placementKey: asString(activeLinkedPlacement?.id || linkedPlacements[0]?.id),
        image: asString(view.imageUrl),
        imageUrl: asString(view.imageUrl),
        fileId: asString(view.mediaId),
        mediaId: asString(view.mediaId),
        alt: asString(view.alt),
        variantIds: asArray(view.variantIds).map(asString).filter(Boolean),
        designEnabled: Boolean(activeLinkedPlacement),
        role: asString(view.role || (linkedPlacements.length ? 'placement' : 'reference')),
        legacyKeys: [id, asString(view.mediaId)].filter(Boolean),
      };
    });

    const zones = placements.flatMap((placement, placementIndex) => {
      const placementId = asString(placement.id || `placement-${placementIndex + 1}`);
      const legacyKeys = Array.from(new Set([
        placementId,
        ...asArray(placement.legacyKeys).map(asString),
      ].filter(Boolean)));

      return asArray(placement.instances).map((instance, instanceIndex) => {
        const instanceId = asString(instance?.id || `${placementId}-instance-${instanceIndex + 1}`);
        const parts = asArray(instance?.parts).map((part, partIndex) => (
          normalizePart(part, `${instanceId}-part-${partIndex + 1}`)
        ));
        const bounds = getBounds(parts);
        return {
          key: instanceId,
          id: instanceId,
          groupId: placementId,
          placementId,
          legacyKeys,
          view: asString(instance?.viewId),
          label: asString(placement.label || placementId),
          ...bounds,
          templateUrl: asString(placement.templateUrl),
          active: !isInactive(placement.active),
          parts,
        };
      });
    });

    const variantMediaV2 = rawConfig.variantMedia && typeof rawConfig.variantMedia === 'object'
      ? rawConfig.variantMedia
      : {};

    return {
      ...rawConfig,
      views,
      zones,
      variantMediaV2,
      variantMedia: buildLegacyVariantMedia(variantMediaV2, placements, mediaViews),
    };
  };

  window.TodayMerchStudioConfigCompat = {
    adapt,
    buildLegacyVariantMedia,
  };
})();
